import { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import ValuationLead from "./ValuationLead";

/*
 * AI-assisted valuation, for the product brand only.
 *
 * WHAT IT IS. The arithmetic valuation is already a real answer: a price per
 * square metre taken from Land Registry sales of comparable properties
 * nearby, indexed to today, multiplied by this property's floor area. The
 * model does not produce that figure and cannot replace it. It reads the
 * details a visitor supplies - tenure, condition, parking, recent works -
 * and proposes an ADJUSTMENT to it, capped server-side.
 *
 * So there are always two numbers on screen and they are always both shown.
 * Hiding the unadjusted figure would turn a traceable estimate into an
 * oracle, which is the one thing a valuation tool must not be.
 *
 * THE DISCLAIMER COMES FROM THE API, deliberately. api/data/aiValuation.js
 * puts it in the payload because three surfaces render this - the Android
 * app and both brands of website - and a disclaimer written out separately
 * in each is a disclaimer that goes stale in two of them. This component
 * renders what it is given and adds a standing warning of its own ABOVE the
 * button, so nobody reaches a figure without having passed the caveat first.
 *
 * WHO SEES IT. Product brand only, which the caller decides from brand.js.
 * The agency sells valuations performed by people; an estate agent's own
 * site offering an automated one beside its instruction fees would be
 * saying two different things at once.
 */

const BOX_RADIUS = "10px";

const Wrap = styled.section`
    margin: 2rem 0 0;
    border: 1px solid ${(props) => props.theme.lightgrey};
    border-radius: ${BOX_RADIUS};
    background: #fff;
    box-shadow: 0 1px 2px rgba(20, 49, 91, 0.06), 0 8px 24px rgba(20, 49, 91, 0.07);
    overflow: hidden;
`;

const Head = styled.header`
    padding: 1.1rem 1.25rem;
    border-bottom: 1px solid #f0f0f0;
    h2 {
        margin: 0;
        font-size: 1.15rem;
        /* index.css sets every h2 to weight 200, which Roboto has no face for
           and the browser resolves to Thin. Stated here, as the panels do. */
        font-weight: 500;
        color: ${(props) => props.theme.darkblue};
    }
    p {
        margin: 0.3rem 0 0;
        font-size: 0.9rem;
        color: ${(props) => props.theme.grey};
    }
`;

const Inner = styled.div`
    padding: 1.25rem;
`;

/*
 * The standing warning, and it is deliberately loud. It sits ABOVE the
 * button rather than beneath the result, because a caveat that only appears
 * next to a number has already lost - the number is read first and
 * remembered, and the caveat is read second and forgotten.
 */
const Warning = styled.div`
    border: 2px solid ${(props) => props.theme.orange};
    border-radius: ${BOX_RADIUS};
    background: #fdf6ea;
    padding: 1rem 1.1rem;
    margin-bottom: 1.25rem;
    strong {
        display: block;
        font-size: 1rem;
        color: #8a5a10;
        margin-bottom: 0.35rem;
    }
    p {
        margin: 0;
        font-size: 0.88rem;
        line-height: 1.5;
        color: #6b4a16;
    }
    p + p {
        margin-top: 0.5rem;
    }
`;

const Fields = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
    gap: 0.9rem;
    margin-bottom: 1.1rem;
`;


const Choice = styled.label`
    display: block;
    min-width: 0;
    span {
        display: block;
        font-size: 0.78rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: ${(props) => props.theme.darkblue};
        margin-bottom: 0.3rem;
    }
    /* The asterisk is the only thing marking a required question, so it has
       to read as deliberate rather than as a stray character. */
    em {
        font-style: normal;
        color: ${(props) => props.theme.red};
        margin-left: 0.15rem;
    }
    select {
        width: 100%;
        box-sizing: border-box;
        padding: 0.6rem 0.7rem;
        font-size: 0.92rem;
        font-family: inherit;
        color: ${(props) => props.theme.black};
        background: #fff;
        border: 1px solid
            ${(props) => (props.unanswered ? props.theme.orange : props.theme.lightgrey)};
        border-radius: ${(props) => props.theme.radius};
        &:focus {
            outline: 2px solid ${(props) => props.theme.lightblue};
            outline-offset: 1px;
            border-color: ${(props) => props.theme.darkblue};
        }
    }
`;

const Notes = styled.label`
    display: block;
    margin-bottom: 1.1rem;
    span {
        display: block;
        font-size: 0.78rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: ${(props) => props.theme.darkblue};
        margin-bottom: 0.3rem;
    }
    textarea {
        width: 100%;
        box-sizing: border-box;
        min-height: 5.5rem;
        resize: vertical;
        padding: 0.6rem 0.7rem;
        font-size: 0.92rem;
        font-family: inherit;
        line-height: 1.5;
        color: ${(props) => props.theme.black};
        border: 1px solid ${(props) => props.theme.lightgrey};
        border-radius: ${(props) => props.theme.radius};
        &:focus {
            outline: 2px solid ${(props) => props.theme.lightblue};
            outline-offset: 1px;
            border-color: ${(props) => props.theme.darkblue};
        }
    }
`;

const GroupHead = styled.h3`
    margin: 1.4rem 0 0.7rem;
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${(props) => props.theme.grey};
`;

/*
 * THE ACKNOWLEDGEMENT, and why it is two ticks rather than one.
 *
 * A disclaimer somebody scrolled past is a disclaimer they can honestly say
 * they never read. Two deliberate ticks are two recorded acts: one that the
 * figure is a guide and not a valuation, one that they will take advice
 * before acting on it. They say different things and a single box would let
 * the second hide behind the first.
 *
 * WHAT THIS DOES AND DOES NOT DO, stated plainly because it would be easy to
 * believe otherwise. It materially helps: a prominent term, separately
 * acknowledged before the service runs, is far more likely to be held
 * transparent and fair than small print under an answer. It does NOT make
 * anything watertight. Under the Consumer Rights Act 2015 a consumer term
 * still has to be fair and transparent to bind at all, and liability for
 * fraud or for death or personal injury caused by negligence cannot be
 * excluded by any wording. The honest description is "materially reduces
 * exposure and makes reliance much harder to claim", not "not responsible".
 */
const Ack = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    margin-bottom: 0.6rem;
    cursor: pointer;
    input {
        margin: 0.15rem 0 0;
        width: 1.15rem;
        height: 1.15rem;
        flex: none;
        accent-color: ${(props) => props.theme.darkblue};
    }
    span {
        font-size: 0.88rem;
        line-height: 1.5;
        color: #6b4a16;
    }
`;

const Run = styled.button`
    padding: 0.85rem 1.8rem;
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
    background: ${(props) => props.theme.darkblue};
    border: 0;
    border-radius: ${(props) => props.theme.radius};
    cursor: pointer;
    transition: opacity 0.14s ease;
    &:hover {
        opacity: 0.9;
    }
    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
`;

const Hint = styled.p`
    margin: 0.7rem 0 0;
    font-size: 0.84rem;
    color: ${(props) => props.theme.grey};
`;

const Err = styled.p`
    margin: 0.9rem 0 0;
    font-size: 0.9rem;
    color: ${(props) => props.theme.red};
`;

const Figures = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
    gap: 1rem;
    margin: 1.25rem 0 0;
`;

const Figure = styled.div`
    border: 1px solid ${(props) => (props.lead ? props.theme.darkblue : props.theme.lightgrey)};
    border-radius: ${BOX_RADIUS};
    padding: 0.9rem 1rem;
    background: ${(props) => (props.lead ? "#f4f8fc" : "transparent")};
    h3 {
        margin: 0 0 0.3rem;
        font-size: 0.78rem;
        font-weight: 700;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: ${(props) => props.theme.grey};
    }
    strong {
        display: block;
        font-size: 1.35rem;
        font-weight: 600;
        color: ${(props) => props.theme.darkblue};
        line-height: 1.2;
    }
    small {
        display: block;
        margin-top: 0.3rem;
        font-size: 0.82rem;
        color: ${(props) => props.theme.grey};
    }
`;

const Section = styled.div`
    margin-top: 1.25rem;
    h4 {
        margin: 0 0 0.4rem;
        font-size: 0.86rem;
        font-weight: 700;
        color: ${(props) => props.theme.darkblue};
    }
    ul {
        margin: 0;
        padding-left: 1.1rem;
        font-size: 0.9rem;
        line-height: 1.55;
        color: ${(props) => props.theme.black};
    }
    p {
        margin: 0;
        font-size: 0.9rem;
        line-height: 1.55;
        color: ${(props) => props.theme.black};
    }
`;

/*
 * The payload's own disclaimer, rendered where the figures are. The standing
 * warning above covers somebody who never pressed the button; this covers
 * the printed page, where the two are the only text that travels with a
 * number to a buyer, a landlord or a solicitor.
 */
/*
 * "Share or save", offered where the valuation actually appears rather than
 * only at the top of a very long report.
 */
const Keep = styled.div`
    margin-top: 1.5rem;
    padding: 1.1rem 1.25rem;
    border: 1px solid #d9e2ec;
    border-radius: ${(props) => props.theme.radius || "4px"};
    background: #f7fafc;

    h3 {
        margin: 0 0 0.25rem;
        font-size: 1rem;
        font-weight: 600;
        color: ${(props) => props.theme.darkblue || "#1b2a41"};
    }

    p {
        margin: 0 0 0.9rem;
        font-size: 0.9rem;
        line-height: 1.45;
        color: #4a5568;
    }
`;

const KeepRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const KeepButton = styled.button`
    padding: 0.7rem 1.1rem;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: inherit;
    color: ${(props) => props.theme.darkblue || "#1b2a41"};
    background: #fff;
    border: 1px solid #c3cfdb;
    border-radius: ${(props) => props.theme.radius || "4px"};
    cursor: pointer;
    transition: all 0.14s ease;

    &:hover {
        border-color: ${(props) => props.theme.darkblue || "#1b2a41"};
        background: #eef3f8;
    }
    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.darkblue || "#1b2a41"};
        outline-offset: 2px;
    }
`;

const Legal = styled.div`
    margin-top: 1.25rem;
    border: 2px solid ${(props) => props.theme.orange};
    border-radius: ${BOX_RADIUS};
    background: #fdf6ea;
    padding: 1rem 1.1rem;
    strong {
        display: block;
        font-size: 0.95rem;
        color: #8a5a10;
        margin-bottom: 0.35rem;
    }
    p {
        margin: 0;
        font-size: 0.86rem;
        line-height: 1.5;
        color: #6b4a16;
    }
`;

/* Whole pounds. The server has already rounded to the nearest £5,000 - this
   only puts the separators in. */
const money = (v) =>
    typeof v === "number" ? "£" + new Intl.NumberFormat("en-GB").format(Math.round(v)) : null;

const range = (e) => (e ? money(e.low) + " – " + money(e.high) : null);

/*
 * THE QUESTIONS, AND WHY THEY ARE LISTS RATHER THAN BOXES.
 *
 * These were free-text boxes, and free text is the wrong instrument for a
 * closed question. "Freehold", "freehold", "FH" and "I think freehold?" are
 * four spellings of one fact, and the model had to guess which it was being
 * told. Worse, an empty box is ambiguous in the one way that matters: it
 * cannot be told apart from "there isn't one", so a property with no parking
 * and a property whose owner could not be bothered looked identical.
 *
 * So every closed question is a list, and every list carries "Not sure".
 * That last option is not padding. Requiring an answer and offering no way
 * to say you do not know is a machine for collecting invented ones, and an
 * invented lease length moves a valuation further than an absent one ever
 * would. "Not sure" reaches the model as a stated unknown, which is what it
 * actually is, and it comes back in "What it could not take into account".
 *
 * REQUIRED means the question must be ANSWERED, not that an answer must be
 * known - which is the distinction that makes forcing them reasonable.
 */
const ASK = [
    {
        key: "bedrooms", label: "Bedrooms", required: true,
        options: ["Studio", "1", "2", "3", "4", "5", "6 or more", "Not sure"],
    },
    {
        key: "bathrooms", label: "Bathrooms", required: true,
        options: ["1", "2", "3", "4 or more", "Not sure"],
    },
    {
        key: "receptions", label: "Reception rooms", required: true,
        options: ["1", "2", "3", "4 or more", "Not sure"],
    },
    {
        key: "tenure", label: "Tenure", required: true,
        options: ["Freehold", "Leasehold", "Share of freehold", "Commonhold", "Not sure"],
    },
    {
        key: "condition", label: "Condition", required: true,
        options: [
            "Newly refurbished throughout",
            "Good — well maintained",
            "Average — liveable, some wear",
            "Dated — needs updating",
            "Needs major work",
            "Not sure",
        ],
    },
    {
        key: "outdoorSpace", label: "Outdoor space", required: true,
        options: [
            "Private garden", "Shared garden", "Patio or terrace", "Balcony",
            "Roof terrace", "None", "Not sure",
        ],
    },
    {
        key: "parking", label: "Parking", required: true,
        options: [
            "Garage", "Driveway", "Allocated space", "Residents' permit",
            "On street, no permit needed", "None", "Not sure",
        ],
    },
    {
        key: "position", label: "Position", required: true,
        options: [
            "Quiet residential street", "Cul-de-sac", "Main road", "Above shops",
            "Ground floor", "Upper floor, with lift", "Upper floor, no lift", "Not sure",
        ],
    },
    {
        key: "recentWorks", label: "Recent works", required: false,
        options: [
            "None", "Cosmetic refresh", "New kitchen or bathroom", "Rewired or new boiler",
            "New windows or insulation", "Extension", "Loft conversion",
            "Several of these", "Not sure",
        ],
    },
];

/*
 * Only asked of a leasehold, because asking a freeholder for their lease
 * length is how a form teaches people that it is not listening. Shown the
 * moment "Leasehold" or "Share of freehold" is chosen - a share of freehold
 * still has a lease, and its length still matters.
 */
const LEASEHOLD_ASK = [
    {
        key: "leaseYears", label: "Years left on the lease", required: true,
        options: ["Over 125", "100 to 125", "80 to 99", "Under 80", "Not sure"],
    },
    {
        key: "groundRent", label: "Ground rent", required: false,
        options: ["None or peppercorn", "Under £250 a year", "£250 to £500", "Over £500", "Not sure"],
    },
    {
        key: "serviceCharge", label: "Service charge", required: false,
        options: ["Under £1,000 a year", "£1,000 to £2,500", "£2,500 to £5,000", "Over £5,000", "Not sure"],
    },
    {
        key: "majorWorks", label: "Major works expected", required: false,
        options: ["None known", "Yes, planned", "Yes, already billed", "Not sure"],
    },
];

/* For the rent figure only, and optional - somebody valuing a sale should
   not have to answer questions about tenants to get a price. */
const LETTING_ASK = [
    {
        key: "furnishing", label: "Furnishing, if let", required: false,
        options: ["Furnished", "Part-furnished", "Unfurnished", "Not sure"],
    },
    {
        key: "targetTenant", label: "Likely tenant", required: false,
        options: ["Professionals or sharers", "Family", "Corporate", "Students", "Not sure"],
    },
];

const needsLease = (tenure) => tenure === "Leasehold" || tenure === "Share of freehold";

export default function AiValuation({
    postcode,
    cert,
    initialToken,
    onToken,
    shareUrl,
    onPrint,
    onEmail,
}) {
    const [details, setDetails] = useState({});
    /*
     * Reset whenever the property changes, deliberately. An acknowledgement
     * is given about a particular property, and carrying it across to the
     * next address would be recording consent nobody gave.
     */
    const [ack, setAck] = useState({ guide: false, advice: false });
    const acknowledged = ack.guide && ack.advice;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    /*
     * A valuation somebody shared with us, fetched rather than re-asked.
     *
     * WHY THIS IS A FETCH AND NOT A RE-RUN. The figure was produced once and
     * paid for once. Re-running it here would spend tokens again and, worse,
     * could print a different number under the same link - the comparable
     * sales underneath move every night. What was shared is what is shown.
     *
     * Runs once per token. A failure is deliberately quiet: the report
     * around it is perfectly good, and an error banner about a valuation
     * nobody asked for would be noise. The form is left ready underneath,
     * so anyone who wants a current figure can produce one.
     */
    useEffect(() => {
        if (!initialToken || !cert || !cert.certificateNumber) return;

        let cancelled = false;
        const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

        api.get("/property-data/valuation/" + encodeURIComponent(initialToken), {
            params: { postcode: postcode, certificate: cert.certificateNumber },
        })
            .then((res) => {
                if (!cancelled) setResult(res.data);
            })
            .catch(() => {
                /* Shared link, expired or wrong property. Leave the form. */
            });

        return () => {
            cancelled = true;
        };
    }, [initialToken, postcode, cert]);

    /*
     * Tell the page which valuation this is, so the share buttons can put it
     * in the link. Null when there is no result, so clearing the panel
     * clears the link too rather than sharing a figure that is no longer on
     * screen.
     */
    const producedToken = (result && result.valuation && result.valuation.token) || null;
    useEffect(() => {
        if (onToken) onToken(producedToken || "");
    }, [producedToken, onToken]);

    const [linkCopied, setLinkCopied] = useState(false);

    async function copyLink() {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (err) {
            // Clipboard refused - insecure context, or permission. Showing
            // the link is the fallback that always works.
            window.prompt("Copy this link", shareUrl);
        }
    }

    /*
     * The email form lives with the other share controls at the top of the
     * report. Rather than build a second one here, this opens that one and
     * takes the reader to it - one form, one piece of validation, one place
     * a change has to be made.
     */
    function emailIt() {
        if (onEmail) onEmail();
    }

    const set = (key, value) => setDetails((d) => Object.assign({}, d, { [key]: value }));

    const showLease = needsLease(details.tenure);
    const asked = ASK;

    /*
     * The required questions that are actually ON SCREEN. The lease ones
     * only count once a lease exists - counting them while they are hidden
     * would leave the button disabled with nothing visible to fix, which is
     * the worst kind of form there is.
     */
    const required = ASK.filter((q) => q.required).concat(
        showLease ? LEASEHOLD_ASK.filter((q) => q.required) : []
    );
    const missing = required.filter((q) => !details[q.key]).length;

    const field = (q) => (
        <Choice key={q.key} unanswered={q.required && !details[q.key]}>
            <span>
                {q.label}
                {q.required && <em title="Required">*</em>}
            </span>
            <select
                id={"val-" + q.key}
                value={details[q.key] || ""}
                onChange={(e) => set(q.key, e.target.value)}
            >
                <option value="">Choose&hellip;</option>
                {q.options.map((o) => (
                    <option key={o} value={o}>
                        {o}
                    </option>
                ))}
            </select>
        </Choice>
    );

    async function run() {
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });
            const res = await api.post("/property-data/valuation/ai", {
                postcode: postcode,
                certificate: cert.certificateNumber,
                address: cert.address,
                /*
                 * What resolves this property's own council tax band rather
                 * than the district's default. Without it the model is told
                 * a band that is a fact about the postcode, not the house.
                 */
                uprn: cert.uprn || undefined,
                details: details,
            });
            setResult(res.data);
        } catch (err) {
            const data = err.response && err.response.data;
            /*
             * A 503 means the feature is switched off because no key is set.
             * That is not a fault the visitor can do anything about, and
             * "try again" would be a lie, so it says what it is.
             */
            setError(
                (data && (data.error || data.detail)) ||
                    "Could not produce a valuation. Please try again in a moment."
            );
        } finally {
            setLoading(false);
        }
    }

    const sale = result && result.sale;
    const ai = result && result.ai;
    const adjusted = ai && ai.status === "ok" ? ai.adjustedEstimate : null;

    return (
        <Wrap>
            <Head>
                <h2>Value this property</h2>
                <p>
                    An estimate from comparable sales, adjusted by a model for what you know about
                    the property.
                </p>
            </Head>

            <Inner>
                <Warning>
                    <strong>This is not a valuation.</strong>
                    <p>
                        It is a guide produced from public records and a language model. It is{" "}
                        <strong
                            style={{ display: "inline", fontSize: "inherit", marginBottom: 0 }}
                        >
                            not
                        </strong>{" "}
                        a RICS valuation, a survey, a mortgage valuation or an estate agent&rsquo;s
                        appraisal, and it must not be relied on for a sale, a purchase, a mortgage,
                        probate, tax, a court or any other decision that matters.
                    </p>
                    <p>
                        Nobody has seen this property. The figure cannot know about its condition,
                        its outlook, its legal title or anything else a person standing in it would
                        notice. Any rent figure is the model&rsquo;s own estimate with no comparable
                        evidence behind it at all. Always take professional advice before acting.
                    </p>
                </Warning>

                <GroupHead>About the property</GroupHead>
                <Fields>{asked.map(field)}</Fields>

                {showLease && (
                    <>
                        <GroupHead>The lease</GroupHead>
                        <Fields>{LEASEHOLD_ASK.map(field)}</Fields>
                    </>
                )}

                <GroupHead>If you were letting it (optional)</GroupHead>
                <Fields>{LETTING_ASK.map(field)}</Fields>

                <Notes>
                    <span>Anything else worth knowing</span>
                    <textarea
                        id="val-notes"
                        value={details.notes || ""}
                        maxLength={1200}
                        placeholder="South-facing garden, backs onto the common, planning granted for a side return, shares a wall with a pub — anything a valuer standing in it would notice."
                        onChange={(e) => set("notes", e.target.value)}
                    />
                </Notes>

                {/*
                  * Inside the warning box, directly under the words being
                  * acknowledged, rather than down beside the button. A tick
                  * that sits away from what it refers to is a tick somebody
                  * can truthfully say they did not connect to anything.
                  */}
                <Warning>
                    <strong>Before you continue</strong>
                    <Ack>
                        <input
                            id="val-ack-guide"
                            type="checkbox"
                            checked={ack.guide}
                            onChange={(e) => setAck((a) => ({ ...a, guide: e.target.checked }))}
                        />
                        <span>
                            I understand this is an <strong>automated guide, not a valuation</strong>,
                            that nobody has inspected the property, and that Doorstep Data accepts no
                            responsibility for any decision taken on the strength of it.
                        </span>
                    </Ack>
                    <Ack>
                        <input
                            id="val-ack-advice"
                            type="checkbox"
                            checked={ack.advice}
                            onChange={(e) => setAck((a) => ({ ...a, advice: e.target.checked }))}
                        />
                        <span>
                            I will take advice from a qualified local agent or surveyor before
                            buying, selling, letting, borrowing against or otherwise acting on this
                            property.
                        </span>
                    </Ack>
                </Warning>

                <Run
                    type="button"
                    onClick={run}
                    disabled={loading || !cert || missing > 0 || !acknowledged}
                >
                    {loading ? "Working it out…" : "Get a valuation"}
                </Run>
                <Hint>
                    {missing > 0 ? (
                        <>
                            <strong>
                                {missing} question{missing === 1 ? "" : "s"} still to answer.
                            </strong>{" "}
                            Choose &ldquo;Not sure&rdquo; for anything you don&rsquo;t know &mdash;
                            that is a real answer and a better one than a guess, and the valuation
                            will say it could not take it into account.
                        </>
                    ) : !acknowledged ? (
                        <strong>Please tick both boxes above to continue.</strong>
                    ) : (
                        <>
                            We already know the floor area, energy rating, council tax band, nearby
                            sales, transport, schools and planning designations from your report
                            &mdash; those are sent for you. This one waits on a model, so it takes a
                            few seconds longer than the rest of the report.
                        </>
                    )}
                </Hint>

                {error && <Err>{error}</Err>}

                {result && (
                    <>
                        {sale && sale.status !== "estimated" ? (
                            <Section>
                                <h4>No estimate</h4>
                                <p>
                                    {sale.reason ||
                                        "There was not enough evidence to value this property."}
                                </p>
                            </Section>
                        ) : (
                            <>
                                <Figures>
                                    <Figure>
                                        <h3>From comparable sales</h3>
                                        <strong>{money(sale.estimate.mid)}</strong>
                                        <small>{range(sale.estimate)}</small>
                                        <small>
                                            {sale.basis.comparablesUsed} nearby sales, indexed to
                                            today
                                        </small>
                                    </Figure>

                                    {adjusted && (
                                        <Figure lead>
                                            <h3>Adjusted for what you told us</h3>
                                            <strong>{money(adjusted.mid)}</strong>
                                            <small>{range(adjusted)}</small>
                                            <small>
                                                {ai.adjustmentPercent > 0 ? "+" : ""}
                                                {ai.adjustmentPercent}% on the figure above
                                                {ai.adjustmentCapped
                                                    ? " (capped at " + ai.maxAdjustment + "%)"
                                                    : ""}
                                                {ai.confidence ? " · " + ai.confidence + " confidence" : ""}
                                            </small>
                                        </Figure>
                                    )}

                                    {ai && ai.status === "ok" && ai.rentPcm && (
                                        <Figure>
                                            <h3>Rent, if let</h3>
                                            {/*
                                             * rentPcm is a RANGE OBJECT - {low, mid, high,
                                             * basis, evidence} - not a number. Reading it as
                                             * one rendered a tile saying "/mo" with no figure
                                             * in front of it, which is how this was caught on
                                             * the live page rather than in review.
                                             */}
                                            <strong>{money(ai.rentPcm.mid)}/mo</strong>
                                            <small>{range(ai.rentPcm)}</small>
                                            {ai.rentPcm.basis && <small>{ai.rentPcm.basis}</small>}
                                            <small>
                                                The model&rsquo;s own estimate &mdash; no comparable
                                                evidence behind this one.
                                            </small>
                                        </Figure>
                                    )}
                                </Figures>

                                {ai && ai.status === "unavailable" && (
                                    <Section>
                                        <h4>The adjustment is not available</h4>
                                        <p>
                                            {ai.reason || "The model did not answer."} The figure
                                            above is the arithmetic estimate from comparable sales,
                                            which stands on its own.
                                        </p>
                                    </Section>
                                )}

                                {ai && ai.status === "ok" && ai.narrative && (
                                    <Section>
                                        <h4>What it made of it</h4>
                                        <p>{ai.narrative}</p>
                                    </Section>
                                )}

                                {ai && ai.status === "ok" && ai.reasons && ai.reasons.length > 0 && (
                                    <Section>
                                        <h4>Why it moved the figure</h4>
                                        <ul>
                                            {ai.reasons.map((r, i) => (
                                                <li key={i}>{r}</li>
                                            ))}
                                        </ul>
                                    </Section>
                                )}

                                {ai &&
                                    ai.status === "ok" &&
                                    ai.missingInformation &&
                                    ai.missingInformation.length > 0 && (
                                        <Section>
                                            <h4>What it could not take into account</h4>
                                            <ul>
                                                {ai.missingInformation.map((m, i) => (
                                                    <li key={i}>{m}</li>
                                                ))}
                                            </ul>
                                        </Section>
                                    )}

                                {sale.caveats && sale.caveats.length > 0 && (
                                    <Section>
                                        <h4>How the comparable figure was reached</h4>
                                        <ul>
                                            {sale.caveats.map((c, i) => (
                                                <li key={i}>{c}</li>
                                            ))}
                                        </ul>
                                    </Section>
                                )}
                            </>
                        )}

                        {ai && ai.disclaimer && (
                            <Legal>
                                <strong>A guide, not a valuation</strong>
                                <p>{ai.disclaimer}</p>
                            </Legal>
                        )}

                        {/*
                          * WHAT TO DO WITH IT, at the point somebody has it.
                          *
                          * The share controls live at the top of the page,
                          * beside the address. That is the right place to
                          * find them and the wrong place to be when the
                          * valuation has just appeared at the foot of a very
                          * long report - scrolling back up to share what you
                          * are looking at is a small, avoidable annoyance.
                          *
                          * The link offered here carries the valuation, so
                          * what arrives is what is on screen.
                          */}
                        <Keep>
                            <h3>Share or save the full report</h3>
                            <p>
                                Everything above, including this valuation, travels with the
                                link and the PDF.
                            </p>
                            <KeepRow>
                                {onPrint && (
                                    <KeepButton type="button" onClick={onPrint}>
                                        Save as PDF
                                    </KeepButton>
                                )}
                                {shareUrl && (
                                    <KeepButton type="button" onClick={copyLink}>
                                        {linkCopied ? "Link copied" : "Copy link"}
                                    </KeepButton>
                                )}
                                <KeepButton type="button" onClick={emailIt}>
                                    Email it
                                </KeepButton>
                            </KeepRow>
                        </Keep>

                        {/*
                          * AFTER the figure and after the disclaimer, never
                          * before. Somebody has just been told to take
                          * professional advice; this is the offer to arrange
                          * it, which is the one moment it is genuinely
                          * useful rather than a toll on the way in.
                          */}
                        <ValuationLead
                            address={cert && cert.address}
                            postcode={postcode}
                            estimate={
                                sale && sale.status === "estimated"
                                    ? money(
                                          adjusted ? adjusted.mid : sale.estimate.mid
                                      )
                                    : null
                            }
                            brandName="Doorstep Data"
                        />
                    </>
                )}
            </Inner>
        </Wrap>
    );
}
